import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Mail, Save, Send, Loader2, CheckCircle2, XCircle, Server } from 'lucide-react';
import { toast } from 'sonner';

export const EmailSettingsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendRecipients, setSendRecipients] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [form, setForm] = useState({
    provider: 'smtp',
    smtp_host: '', smtp_port: '587', smtp_username: '', smtp_password: '',
    from_email: '', from_name: '', encryption: 'tls',
    aws_region: 'us-east-1', aws_access_key: '', aws_secret_key: '',
  });

  useEffect(() => {
    api.get('/email-settings').then(res => {
      if (res.data.success && res.data.data) {
        setForm(prev => ({ ...prev, ...res.data.data, smtp_port: String(res.data.data.smtp_port || '587') }));
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.post('/email-settings', form);
      if (res.data.success) toast.success('Email settings saved');
      else toast.error('Failed to save');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/email-settings/test', form);
      setTestResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.message || 'Connection test failed';
      setTestResult({ success: false, message: msg });
      toast.error(msg);
    }
    finally { setTesting(false); }
  };

  const handleOpenSendModal = () => {
    setSendRecipients(user?.email || '');
    setSendResult(null);
    setSendModalOpen(true);
  };

  const handleSendTestEmail = async () => {
    if (!sendRecipients.trim()) { toast.error('Please enter at least one email'); return; }
    setSending(true);
    setSendResult(null);
    try {
      const res = await api.post('/email-settings/send-test', { recipients: sendRecipients });
      setSendResult(res.data);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send test email';
      setSendResult({ success: false, message: msg });
      toast.error(msg);
    } finally { setSending(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-settings-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold">Email Configuration</h1><p className="text-muted-foreground">Configure outgoing email settings (SMTP / AWS SES)</p></div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleOpenSendModal} data-testid="send-test-email-btn">
            <Mail className="h-4 w-4 mr-2" />Send Test Email
          </Button>
          <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="test-email-btn">
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Test Connection
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-email-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>

      {testResult && (
        <Card className={testResult.success ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'} data-testid="test-result">
          <CardContent className="p-4 flex items-center gap-3">
            {testResult.success ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-red-500" />}
            <p className={`text-sm font-medium ${testResult.success ? 'text-emerald-500' : 'text-red-500'}`}>{testResult.message}</p>
          </CardContent>
        </Card>
      )}

      {/* Provider Selection */}
      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Mail className="h-5 w-5 text-primary" />Provider</CardTitle></CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, provider: 'smtp' }))}
              className={`flex-1 p-4 rounded-lg border text-sm font-medium transition-colors flex items-center gap-3 ${form.provider === 'smtp' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-muted/20'}`}
              data-testid="provider-smtp"
            >
              <Server className="h-5 w-5" />
              <div className="text-left"><p className="font-semibold">SMTP</p><p className="text-xs opacity-70">Standard SMTP server</p></div>
            </button>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, provider: 'aws_ses' }))}
              className={`flex-1 p-4 rounded-lg border text-sm font-medium transition-colors flex items-center gap-3 ${form.provider === 'aws_ses' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:bg-muted/20'}`}
              data-testid="provider-aws"
            >
              <Mail className="h-5 w-5" />
              <div className="text-left"><p className="font-semibold">AWS SES</p><p className="text-xs opacity-70">Amazon Simple Email Service</p></div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* SMTP Settings */}
      {form.provider === 'smtp' && (
        <Card data-testid="smtp-settings">
          <CardHeader><CardTitle className="text-lg">SMTP Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>SMTP Host *</Label><Input value={form.smtp_host} onChange={e => setForm(f => ({ ...f, smtp_host: e.target.value }))} placeholder="e.g. smtp.gmail.com" data-testid="smtp-host" /></div>
              <div className="space-y-2"><Label>SMTP Port</Label><Input type="number" value={form.smtp_port} onChange={e => setForm(f => ({ ...f, smtp_port: e.target.value }))} data-testid="smtp-port" /></div>
              <div className="space-y-2"><Label>Username *</Label><Input value={form.smtp_username} onChange={e => setForm(f => ({ ...f, smtp_username: e.target.value }))} data-testid="smtp-username" /></div>
              <div className="space-y-2"><Label>Password</Label><Input type="password" value={form.smtp_password} onChange={e => setForm(f => ({ ...f, smtp_password: e.target.value }))} data-testid="smtp-password" /></div>
              <div className="space-y-2"><Label>From Email</Label><Input type="email" value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} placeholder="noreply@myzow.com" data-testid="smtp-from-email" /></div>
              <div className="space-y-2"><Label>From Name</Label><Input value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} placeholder="Myzow Solutions" data-testid="smtp-from-name" /></div>
              <div className="space-y-2"><Label>Encryption</Label>
                <Select value={form.encryption} onValueChange={v => setForm(f => ({ ...f, encryption: v }))}>
                  <SelectTrigger data-testid="smtp-encryption"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tls">TLS (Port 587)</SelectItem>
                    <SelectItem value="ssl">SSL (Port 465)</SelectItem>
                    <SelectItem value="none">None (Port 25)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AWS SES Settings */}
      {form.provider === 'aws_ses' && (
        <Card data-testid="aws-settings">
          <CardHeader><CardTitle className="text-lg">AWS SES Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>AWS Region</Label>
                <Select value={form.aws_region} onValueChange={v => setForm(f => ({ ...f, aws_region: v }))}>
                  <SelectTrigger data-testid="aws-region"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                    <SelectItem value="us-west-2">US West (Oregon)</SelectItem>
                    <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                    <SelectItem value="ap-south-1">Asia Pacific (Mumbai)</SelectItem>
                    <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Access Key ID *</Label><Input value={form.aws_access_key} onChange={e => setForm(f => ({ ...f, aws_access_key: e.target.value }))} data-testid="aws-access-key" /></div>
              <div className="space-y-2"><Label>Secret Access Key</Label><Input type="password" value={form.aws_secret_key} onChange={e => setForm(f => ({ ...f, aws_secret_key: e.target.value }))} data-testid="aws-secret-key" /></div>
              <div className="space-y-2"><Label>From Email</Label><Input type="email" value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} placeholder="noreply@myzow.com" data-testid="aws-from-email" /></div>
              <div className="space-y-2"><Label>From Name</Label><Input value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} placeholder="Myzow Solutions" /></div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Send Test Email Modal */}
      <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" />Send Test Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Send a test email using your saved SMTP configuration to verify everything is working.</p>
            <div className="space-y-2">
              <Label>Recipient Email(s)</Label>
              <Input
                value={sendRecipients}
                onChange={e => setSendRecipients(e.target.value)}
                placeholder="email@example.com, another@example.com"
                data-testid="send-test-recipients"
              />
              <p className="text-xs text-muted-foreground">Separate multiple emails with commas</p>
            </div>

            {sendResult && (
              <div className={`p-3 rounded-lg border ${sendResult.success ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`} data-testid="send-test-result">
                <div className="flex items-center gap-2">
                  {sendResult.success ? <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" /> : <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                  <p className={`text-sm ${sendResult.success ? 'text-emerald-500' : 'text-red-500'}`}>{sendResult.message}</p>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSendTestEmail} disabled={sending || !sendRecipients.trim()} data-testid="send-test-submit-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
